import { Fragment, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { apiFetch, API_BASE } from '../lib/api';

// ACT.A5 (#1706) — Faculty Supervisor Cohort Dashboard.
//
// PHASE_LABELS mirrors ClientDashboard.tsx's existing map exactly (same
// sdlc_phase enum) — current_phase is returned verbatim by the API (Q1),
// this is presentation-only.

const PHASE_LABELS: Record<string, string> = {
  phase_0_bootstrap:       'Bootstrap',
  phase_1_requirements:    'Requirements',
  phase_2_architecture:    'Architecture',
  phase_2_5_ux_design:     'UX Design',
  phase_3_sprint_planning: 'Sprint Planning',
  phase_4_development:     'Development',
  phase_5_code_review:     'Code Review',
  phase_6_testing:         'Testing',
  phase_7_deployment:      'Deployment',
  phase_8_retrospective:   'Retrospective',
};

const GATE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  PASS:    { bg: 'rgba(34,197,94,0.12)',  text: '#16a34a', label: 'Pass' },
  FAIL:    { bg: 'rgba(239,68,68,0.12)',  text: '#dc2626', label: 'Fail' },
  PENDING: { bg: 'rgba(245,158,11,0.12)', text: '#d97706', label: 'Pending' },
};

interface TeamRow {
  team_id: string;
  team_name: string;
  project_id: string | null;
  current_phase: string | null;
  last_active_at: string | null;
  gate_status: 'PASS' | 'FAIL' | 'PENDING' | null;
  token_consumed_usd: number;
  token_allocated_usd: number | null;
  pending_client_approvals: number;
}

interface TimelineEvent {
  type: 'gate_result' | 'client_approval';
  occurred_at: string;
  phase: string | null;
  gate_type: string | null;
  result: string | null;
  actor_id: string | null;
}

interface TokenAlert { id: string; entity_id: string; threshold: number; year: number; month: number; created_at: string; }
interface GateFailureAlert { project_id: string; team_id: string; team_name: string; phase: string; gate_type: string; evaluated_at: string; }

interface BurnPoint { team_id: string; team_name: string; date: string; cost_usd: number; }

const CHART_COLORS = ['#7c3aed', '#0891b2', '#16a34a', '#d97706', '#dc2626', '#4f46e5'];

function formatDate(iso: string | null): string {
  if (!iso) return 'No activity yet';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function CohortDashboardPage() {
  const { cohortId } = useParams<{ cohortId: string }>();

  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [tokenAlerts, setTokenAlerts] = useState<TokenAlert[]>([]);
  const [gateFailures, setGateFailures] = useState<GateFailureAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);

  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => isoDate(new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000)), [today]);
  const [rangeStart, setRangeStart] = useState(defaultStart);
  const [rangeEnd, setRangeEnd] = useState(isoDate(today));
  const [burnPoints, setBurnPoints] = useState<BurnPoint[]>([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!cohortId) return;
    loadDashboard();
    loadAlerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId]);

  useEffect(() => {
    if (!cohortId) return;
    loadBurnSeries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId, rangeStart, rangeEnd]);

  async function loadDashboard() {
    try {
      setLoading(true);
      const res = await apiFetch<{ teams: TeamRow[] }>(`/api/academic/cohorts/${cohortId}/dashboard`);
      setTeams(res.teams);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  async function loadAlerts() {
    try {
      const res = await apiFetch<{ token_alerts: TokenAlert[]; gate_failures: GateFailureAlert[] }>(`/api/academic/cohorts/${cohortId}/alerts`);
      setTokenAlerts(res.token_alerts);
      setGateFailures(res.gate_failures);
    } catch {
      // Non-fatal — the table is the primary surface.
    }
  }

  async function loadBurnSeries() {
    try {
      const res = await apiFetch<{ points: BurnPoint[] }>(`/api/academic/cohorts/${cohortId}/token-burn?start=${rangeStart}&end=${rangeEnd}`);
      setBurnPoints(res.points);
    } catch {
      setBurnPoints([]);
    }
  }

  async function toggleTeamRow(teamId: string) {
    if (expandedTeamId === teamId) {
      setExpandedTeamId(null);
      return;
    }
    setExpandedTeamId(teamId);
    setTimelineLoading(true);
    try {
      const res = await apiFetch<{ events: TimelineEvent[] }>(`/api/academic/cohorts/${cohortId}/teams/${teamId}/timeline`);
      setTimeline(res.events);
    } catch {
      setTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  }

  async function acknowledgeTokenAlert(alertId: string) {
    await apiFetch(`/api/academic/cohorts/${cohortId}/alerts/${alertId}/acknowledge`, { method: 'POST' });
    setTokenAlerts((prev) => prev.filter((a) => a.id !== alertId));
  }

  async function acknowledgeGateFailure(g: GateFailureAlert) {
    await apiFetch(`/api/academic/cohorts/${cohortId}/teams/${g.team_id}/gate-failures/acknowledge`, {
      method: 'POST',
      body: JSON.stringify({ phase: g.phase, gateType: g.gate_type }),
    });
    setGateFailures((prev) => prev.filter((x) => !(x.project_id === g.project_id && x.phase === g.phase && x.gate_type === g.gate_type)));
  }

  async function exportWeeklyReport() {
    setExporting(true);
    try {
      const token = localStorage.getItem('blueprint_token');
      const res = await fetch(`${API_BASE}/api/academic/cohorts/${cohortId}/report`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cohort-${cohortId}-report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }

  // Recharts wants one row per date, columns per team.
  const chartData = useMemo(() => {
    const byDate = new Map<string, Record<string, number | string>>();
    for (const p of burnPoints) {
      const row = byDate.get(p.date) ?? { date: p.date };
      row[p.team_name] = p.cost_usd;
      byDate.set(p.date, row);
    }
    return [...byDate.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  }, [burnPoints]);
  const teamNamesInChart = useMemo(() => [...new Set(burnPoints.map((p) => p.team_name))], [burnPoints]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading...</div>;
  if (error) return <div style={{ padding: 40, color: '#ef4444' }}>{error}</div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
            Cohort Dashboard
          </h1>
          <p style={{ fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', marginTop: 6, fontSize: 14 }}>
            Status of every team in this cohort.
          </p>
        </div>
        <button
          onClick={exportWeeklyReport}
          disabled={exporting}
          style={{
            padding: '10px 16px', fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
            color: 'white', background: 'var(--primary)', border: 'none', borderRadius: 'var(--radius-sm)',
            cursor: exporting ? 'default' : 'pointer', opacity: exporting ? 0.6 : 1,
          }}
        >
          {exporting ? 'Generating…' : 'Export Weekly Report (PDF)'}
        </button>
      </div>

      {/* ── Alert Inbox ──────────────────────────────────────────────── */}
      {(tokenAlerts.length > 0 || gateFailures.length > 0) && (
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Alert Inbox</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tokenAlerts.map((a) => (
              <div key={a.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                borderLeft: '4px solid #d97706', background: 'var(--surface)',
              }}>
                <span style={{ fontSize: 13, color: 'var(--text)' }}>
                  {a.entity_id === 'POOL' ? 'Overall token pool' : `Team ${teams.find((t) => t.team_id === a.entity_id)?.team_name ?? a.entity_id}`}
                  {' '}crossed {a.threshold}% of its token budget ({a.year}-{String(a.month).padStart(2, '0')})
                </span>
                <button onClick={() => acknowledgeTokenAlert(a.id)} style={dismissButtonStyle}>Acknowledge</button>
              </div>
            ))}
            {gateFailures.map((g) => (
              <div key={`${g.project_id}-${g.phase}-${g.gate_type}`} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                borderLeft: '4px solid #dc2626', background: 'var(--surface)',
              }}>
                <span style={{ fontSize: 13, color: 'var(--text)' }}>
                  {g.team_name}: {PHASE_LABELS[g.phase] ?? g.phase} ({g.gate_type}) gate failed on {formatDate(g.evaluated_at)}
                </span>
                <button onClick={() => acknowledgeGateFailure(g)} style={dismissButtonStyle}>Acknowledge</button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Team Table ───────────────────────────────────────────────── */}
      <section style={{ marginBottom: 32 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
              <th style={thStyle}>Team</th>
              <th style={thStyle}>Phase</th>
              <th style={thStyle}>Last Active</th>
              <th style={thStyle}>Gate Status</th>
              <th style={thStyle}>Tokens</th>
              <th style={thStyle}>Pending Approvals</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((t) => {
              const gate = t.gate_status ? GATE_COLORS[t.gate_status] : null;
              const pct = t.token_allocated_usd ? Math.min(100, Math.round((t.token_consumed_usd / t.token_allocated_usd) * 100)) : null;
              const expanded = expandedTeamId === t.team_id;
              return (
                <Fragment key={t.team_id}>
                  <tr
                    onClick={() => toggleTeamRow(t.team_id)}
                    style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  >
                    <td style={tdStyle}>{t.team_name}</td>
                    <td style={tdStyle}>{t.current_phase ? (PHASE_LABELS[t.current_phase] ?? t.current_phase) : 'Not started'}</td>
                    <td style={tdStyle}>{formatDate(t.last_active_at)}</td>
                    <td style={tdStyle}>
                      {gate ? (
                        <span style={{ padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: gate.bg, color: gate.text }}>
                          {gate.label}
                        </span>
                      ) : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                    </td>
                    <td style={tdStyle}>
                      {t.token_allocated_usd !== null ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 80, height: 6, borderRadius: 3, background: 'var(--border)', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', background: (pct ?? 0) >= 100 ? '#dc2626' : (pct ?? 0) >= 80 ? '#d97706' : '#16a34a' }} />
                          </div>
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            ${t.token_consumed_usd.toFixed(2)} / ${t.token_allocated_usd.toFixed(2)}
                          </span>
                        </div>
                      ) : <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>${t.token_consumed_usd.toFixed(2)} consumed</span>}
                    </td>
                    <td style={tdStyle}>
                      {t.pending_client_approvals > 0 ? (
                        <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: 'rgba(124,58,237,0.12)', color: 'var(--primary)' }}>
                          {t.pending_client_approvals}
                        </span>
                      ) : <span style={{ color: 'var(--text-secondary)' }}>0</span>}
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={6} style={{ padding: '12px 8px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}>
                        {timelineLoading ? (
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Loading timeline…</span>
                        ) : timeline.length === 0 ? (
                          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>No phase history yet.</span>
                        ) : (
                          <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {timeline.map((e, i) => (
                              <li key={i} style={{ fontSize: 12, color: 'var(--text)', display: 'flex', gap: 8 }}>
                                <span style={{ color: 'var(--text-secondary)', minWidth: 90 }}>{formatDate(e.occurred_at)}</span>
                                {e.type === 'gate_result' ? (
                                  <span>{PHASE_LABELS[e.phase ?? ''] ?? e.phase} ({e.gate_type}) — {e.result}</span>
                                ) : (
                                  <span>Client approval — {e.phase ? (PHASE_LABELS[e.phase] ?? e.phase) : ''} ({e.result ?? 'PENDING'})</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* ── Token Burn Chart ─────────────────────────────────────────── */}
      <section>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', margin: 0 }}>Token Burn</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} style={dateInputStyle} />
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>to</span>
            <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} style={dateInputStyle} />
          </div>
        </div>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              {teamNamesInChart.map((name, i) => (
                <Line key={name} type="monotone" dataKey={name} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}

const thStyle: CSSProperties = { padding: '8px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5 };
const tdStyle: CSSProperties = { padding: '10px 8px', color: 'var(--text)' };
const dismissButtonStyle: CSSProperties = {
  padding: '4px 12px', fontSize: 12, fontWeight: 600, color: 'var(--text)', background: 'var(--surface)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
};
const dateInputStyle: CSSProperties = {
  padding: '4px 8px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
  background: 'var(--surface)', color: 'var(--text)',
};
